import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BsExclamationTriangleFill,
  BsArrowLeftShort,
  BsHouseDoorFill,
} from "react-icons/bs";

const NotFound404 = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Route Disruption Matrix (404) | NGO Waters";
  }, []);

  const fluidSpring = { type: "spring", stiffness: 45, damping: 15 };
  const microSpring = { type: "spring", stiffness: 300, damping: 20 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden d-flex align-items-center justify-content-center bg-light py-5 px-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Dynamic Accent Blobs for Depth */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "500px",
          height: "500px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-150px",
          left: "-50px",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "-100px",
          right: "-50px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative z-1">
        <div className="row justify-content-center text-center">
          <div className="col-md-9 col-lg-7 col-xl-6">
            {/* Visual Error Frame Node */}
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={fluidSpring}
              className="card border-0 bg-white p-4 p-sm-5 shadow-sm"
              style={{
                borderRadius: "28px",
                border: "1px solid rgba(0,0,0,0.02)",
              }}
            >
              {/* Alert Indicator Wrapper */}
              <div className="d-flex justify-content-center mb-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-danger"
                  style={{
                    backgroundColor: "rgba(220, 53, 69, 0.05)",
                    width: "54px",
                    height: "54px",
                  }}
                >
                  <BsExclamationTriangleFill className="fs-4" />
                </div>
              </div>

              {/* 404 Header String */}
              <h1
                className="display-1 fw-black m-0"
                style={{
                  color: "var(--brand-deep-blue)",
                  fontWeight: "900",
                  letterSpacing: "-4px",
                  lineHeight: "0.9",
                  background:
                    "linear-gradient(180deg, var(--brand-deep-blue) 40%, rgba(26,60,107,0.7) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                404
              </h1>

              <h3
                className="fw-bold text-dark mt-3 mb-2"
                style={{ letterSpacing: "-0.5px", fontSize: "1.35rem" }}
              >
                Route Disruption Matrix
              </h3>

              <p
                className="text-muted small mx-auto mb-4"
                style={{ maxWidth: "420px", lineHeight: "1.6" }}
              >
                The terminal coordinate you requested does not map to any active
                ecosystem blueprints. It might have been relocated or purged
                systematically.
              </p>

              {/* Action Operations Panel */}
              <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 pt-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={microSpring}
                  onClick={() => navigate(-1)}
                  className="btn btn-outline-dark py-2.5 px-4 d-inline-flex align-items-center justify-content-center gap-1 fw-semibold"
                  style={{ borderRadius: "12px", fontSize: "0.85rem" }}
                >
                  <BsArrowLeftShort className="fs-5" /> Rollback Step
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={microSpring}
                  onClick={() => navigate("/")}
                  className="btn text-white py-2.5 px-4 d-inline-flex align-items-center justify-content-center gap-2 fw-semibold border-0"
                  style={{
                    backgroundColor: "var(--brand-deep-blue)",
                    borderRadius: "12px",
                    fontSize: "0.85rem",
                  }}
                >
                  <BsHouseDoorFill style={{ fontSize: "0.95rem" }} /> Return
                  Home Core
                </motion.button>
              </div>
            </motion.div>

            {/* Corporate Identification Bracket Footer */}
            <div className="mt-4 pt-2">
              <span
                className="text-uppercase tracking-wider fw-bold text-muted"
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "1.5px",
                  opacity: "0.6",
                }}
              >
                Ponia Food And Beverage Pvt. Ltd. • Compliance Log System
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NotFound404;
